// src/contexts/DirtyFormPromptContext.tsx
//
// Cross-cutting "you have unsaved changes" registry. Forms call
// `useUnsavedChangesPrompt({ id, label, dirty, save })` to register
// their dirty state; navigation actions (bucket / mode rail clicks,
// browser tab close) consult the registry before letting the user
// leave.
//
// The provider is mounted once near the root of the workspace shell
// (`LeadWorkspaceShell`). Three pieces collaborate:
//
//   1. Forms register via the hook in `src/hooks/useUnsavedChangesPrompt.ts`.
//      Each registration is keyed by a stable string id so re-renders
//      don't churn the map.
//   2. The navigation guard (`guardNavigation`) inspects the registry,
//      surfaces a Save / Discard / Cancel modal via the provider's
//      modal state, and routes the user's choice back to the original
//      navigation request via a callback.
//   3. A `beforeunload` listener attached at provider mount fires the
//      browser's native "Leave site?" prompt whenever the registry has
//      any dirty entry — covers tab close / refresh / external link.
//
// Slice 1 of the unsaved-changes work only opts Org Settings in (per
// docs plan slice A). Future slices add more forms by sprinkling the
// hook; no changes to the provider needed.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type DirtyFormEntry = {
  /** Stable unique id, e.g. `"org-settings:branding"`. */
  id: string;
  /** Short human-readable label used in the prompt copy, e.g. `"Org Settings"`. */
  label: string;
  /** `true` while the form has user changes that haven't been persisted. */
  dirty: boolean;
  /**
   * Optional async save callback. When provided, the "Save & continue"
   * button in the prompt modal runs it and only navigates on success.
   * When omitted, the prompt collapses to a "Leave / Cancel" pair.
   */
  save?: () => Promise<{ ok: boolean; error?: string } | void>;
};

type PendingNavigation = {
  /** Runs after the user chooses to proceed (Save & continue → success, or Discard). */
  proceed: () => void;
};

type ModalState =
  | { open: false }
  | {
      open: true;
      pending: PendingNavigation;
      dirtyEntries: DirtyFormEntry[];
    };

type DirtyFormPromptContextValue = {
  register: (entry: DirtyFormEntry) => void;
  unregister: (id: string) => void;
  /** Returns the current dirty entries (snapshot — does not subscribe). */
  snapshotDirty: () => DirtyFormEntry[];
  /**
   * Wrap a navigation request. If anything is dirty, opens the modal
   * and only invokes `proceed` after the user chooses Save & continue
   * (success) or Discard. If nothing is dirty, invokes `proceed`
   * synchronously.
   */
  guardNavigation: (proceed: () => void) => void;
};

const DirtyFormPromptContext = createContext<DirtyFormPromptContextValue | null>(null);

export function useDirtyFormPromptOptional(): DirtyFormPromptContextValue | null {
  return useContext(DirtyFormPromptContext);
}

export function useDirtyFormPrompt(): DirtyFormPromptContextValue {
  const ctx = useContext(DirtyFormPromptContext);
  if (!ctx) {
    throw new Error("useDirtyFormPrompt called outside DirtyFormPromptProvider");
  }
  return ctx;
}

export type DirtyFormPromptProviderProps = {
  children: ReactNode;
  /** Optional override for the modal renderer (tests). */
  renderModal?: (state: ModalState, actions: ModalActions) => ReactNode;
};

export type ModalActions = {
  onCancel: () => void;
  onDiscard: () => void;
  onSaveAndContinue: () => Promise<void>;
};

export function DirtyFormPromptProvider({
  children,
  renderModal,
}: DirtyFormPromptProviderProps) {
  // Refs (not state) so register/unregister don't trigger re-renders of
  // every form using the provider. The modal state is React state so
  // the modal can re-render on open/close.
  const entriesRef = useRef<Map<string, DirtyFormEntry>>(new Map());
  const [modal, setModal] = useState<ModalState>({ open: false });

  const register = useCallback((entry: DirtyFormEntry) => {
    entriesRef.current.set(entry.id, entry);
  }, []);

  const unregister = useCallback((id: string) => {
    entriesRef.current.delete(id);
  }, []);

  const snapshotDirty = useCallback((): DirtyFormEntry[] => {
    return Array.from(entriesRef.current.values()).filter((e) => e.dirty);
  }, []);

  const guardNavigation = useCallback(
    (proceed: () => void) => {
      const dirty = Array.from(entriesRef.current.values()).filter((e) => e.dirty);
      if (dirty.length === 0) {
        proceed();
        return;
      }
      setModal({ open: true, pending: { proceed }, dirtyEntries: dirty });
    },
    [],
  );

  // Browser-level safety net. `beforeunload` returnValue triggers the
  // native "Leave site?" prompt on tab close / refresh / external nav.
  // Modern browsers ignore the custom message string but still show
  // the prompt; the empty-string truthy check is the canonical pattern.
  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      const hasDirty = Array.from(entriesRef.current.values()).some((entry) => entry.dirty);
      if (!hasDirty) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const modalActions: ModalActions = useMemo(
    () => ({
      onCancel: () => setModal({ open: false }),
      onDiscard: () => {
        if (modal.open) {
          // Forms reset their own dirty state on next load — the
          // discard path simply marks each registered entry as
          // not-dirty so the post-navigation guard sees a clean
          // registry. Forms are still mounted at this point; we mutate
          // their stored entry so a subsequent guardNavigation in the
          // same tick wouldn't double-prompt.
          for (const entry of modal.dirtyEntries) {
            const live = entriesRef.current.get(entry.id);
            if (live) {
              entriesRef.current.set(entry.id, { ...live, dirty: false });
            }
          }
          const { proceed } = modal.pending;
          setModal({ open: false });
          proceed();
        }
      },
      onSaveAndContinue: async () => {
        if (!modal.open) return;
        for (const entry of modal.dirtyEntries) {
          if (!entry.save) continue;
          try {
            const result = await entry.save();
            if (result && "ok" in result && !result.ok) {
              // Stop on first failure; keep the modal open so the user
              // sees the in-form error message and can retry / cancel.
              return;
            }
          } catch {
            // Same as ok:false — keep modal open.
            return;
          }
        }
        const { proceed } = modal.pending;
        setModal({ open: false });
        proceed();
      },
    }),
    [modal],
  );

  const value: DirtyFormPromptContextValue = useMemo(
    () => ({ register, unregister, snapshotDirty, guardNavigation }),
    [register, unregister, snapshotDirty, guardNavigation],
  );

  return (
    <DirtyFormPromptContext.Provider value={value}>
      {children}
      {renderModal ? renderModal(modal, modalActions) : null}
    </DirtyFormPromptContext.Provider>
  );
}
